import { useEffect } from 'react'

/**
 * TableScrollHint (PB-21) - tells a visitor that a wide table scrolls sideways.
 *
 * On a phone the comparison tables (/industries, /trust, /compliance, /insights,
 * /security-packages and a blog article) sit in a horizontally scrolling frame,
 * and nothing showed that - the last columns were simply cut off ("Frameworks:
 * SOC 2, PCI DSS, PIPED..."). For every .overflow-x-auto frame that holds a
 * table this component:
 *   - wraps it in .pb-scroll-wrap and keeps three state classes on the wrapper:
 *     .pb-scroll-can (there is more to see), .pb-scroll-start / .pb-scroll-end;
 *   - appends a one-line caption ("Scroll sideways to see more →");
 * and StyleOverrides.jsx (TABLE SCROLL HINT) fades the cut edge and shows the
 * caption only while more is hidden - on a wide screen, where the table fits,
 * neither is visible, and both disappear once the table is scrolled to its end.
 *
 * Pages mount lazily (see HtmlPage.jsx), so this re-scans on pb-pages-mounted
 * and on DOM changes, and re-measures on resize. Idempotent: a frame that is
 * already wrapped is only re-measured.
 */
const NOTE = 'Scroll sideways to see more'

function measure(box) {
  const wrap = box.parentElement
  if (!wrap || !wrap.classList.contains('pb-scroll-wrap')) return
  const can = box.scrollWidth > box.clientWidth + 2
  wrap.classList.toggle('pb-scroll-can', can)
  wrap.classList.toggle('pb-scroll-start', box.scrollLeft <= 2)
  wrap.classList.toggle('pb-scroll-end', box.scrollLeft + box.clientWidth >= box.scrollWidth - 2)
}

function enhance(box) {
  if (box.dataset.pbScrollHint) return
  if (!box.querySelector('table')) return
  box.dataset.pbScrollHint = '1'
  const wrap = document.createElement('div')
  wrap.className = 'pb-scroll-wrap'
  box.parentNode.insertBefore(wrap, box)
  wrap.appendChild(box)
  const note = document.createElement('p')
  note.className = 'pb-scroll-note'
  note.setAttribute('aria-hidden', 'true')
  note.innerHTML = NOTE + ' <span>→</span>'
  wrap.appendChild(note)
  box.addEventListener('scroll', () => measure(box), { passive: true })
  if ('ResizeObserver' in window) new ResizeObserver(() => measure(box)).observe(box)
  measure(box)
}

export default function TableScrollHint() {
  useEffect(() => {
    let raf = 0
    const scan = () => {
      raf = 0
      document.querySelectorAll('.overflow-x-auto').forEach(enhance)
      document.querySelectorAll('.pb-scroll-wrap > .overflow-x-auto').forEach(measure)
    }
    const schedule = () => { if (!raf) raf = requestAnimationFrame(scan) }
    scan()
    const mo = new MutationObserver(schedule)
    mo.observe(document.body, { childList: true, subtree: true })
    window.addEventListener('resize', schedule)
    window.addEventListener('pb-pages-mounted', schedule)
    return () => {
      mo.disconnect()
      window.removeEventListener('resize', schedule)
      window.removeEventListener('pb-pages-mounted', schedule)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])
  return null
}
